import type { FunnelConfig, FunnelSection } from '../types/onboarding.ts';

type TemplateDesignFunnelState = Pick<
  FunnelConfig,
  | 'designTheme'
  | 'designStyleSheet'
  | 'designPalette'
  | 'designTemplateName'
  | 'designTemplateType'
  | 'designPrimaryColor'
  | 'designSecondaryColor'
  | 'designCoverStyle'
  | 'designTemplateContractVersion'
  | 'designTemplateFamilyMetadata'
  | 'universalStyleEngineVersion'
  | 'universalCatalogReleaseId'
  | 'universalPageStyleId'
  | 'universalCreationRequestId'
>;

export const buildOnboardingTemplateDesignPayload = (
  funnel: TemplateDesignFunnelState,
) => {
  if (funnel.universalStyleEngineVersion === 'universal_slots_v1') return undefined;
  if (!funnel.designStyleSheet) return undefined;

  return {
    theme: funnel.designTheme || funnel.designStyleSheet,
    primary_color: funnel.designPrimaryColor || '#FFFFFF',
    secondary_color: funnel.designSecondaryColor || '#FFFFFF',
    template_name: funnel.designTemplateName || funnel.designTemplateType || 'Template Studio',
    cover_style: funnel.designCoverStyle || 'template_studio_html',
    style_sheet: funnel.designStyleSheet,
    palette: funnel.designPalette || 'red',
    template_family: funnel.designTemplateType || 'sophisticated-html-v2',
    contract_version: funnel.designTemplateContractVersion || undefined,
    template_family_metadata: funnel.designTemplateFamilyMetadata || {},
  };
};

export const buildUniversalStyleSelectionPayload = (
  funnel: Pick<
    FunnelConfig,
    | 'universalStyleEngineVersion'
    | 'universalCatalogReleaseId'
    | 'universalPageStyleId'
    | 'universalCreationRequestId'
    | 'sections'
  >,
) => {
  if (
    funnel.universalStyleEngineVersion !== 'universal_slots_v1' ||
    !funnel.universalCatalogReleaseId ||
    !funnel.universalPageStyleId ||
    !funnel.universalCreationRequestId
  ) return undefined;

  const enabledComposition = funnel.sections
    .filter((section) => section.enabled)
    .sort((left, right) => left.order - right.order);
  if (
    enabledComposition.length === 0 ||
    enabledComposition.some((section) =>
      !section.universalInstanceKey ||
      !section.sectionType ||
      !section.universalTargetDesignId)
  ) return undefined;

  return {
    style_engine_version: 'universal_slots_v1' as const,
    catalog_release_id: funnel.universalCatalogReleaseId,
    page_style_id: funnel.universalPageStyleId,
    creation_request_id: funnel.universalCreationRequestId,
    // Style defaults only seed the wizard. The user's final exact
    // composition is the creation authority; canonical HTML stays server-owned.
    composition: enabledComposition.map((section) => ({
      instance_key: section.universalInstanceKey!,
      section_role: section.sectionType!,
      target_design_id: section.universalTargetDesignId!,
    })),
  };
};

export const invalidatePreparedTemplateSections = (
  sections: FunnelSection[],
) => sections.filter((section) => section.isStandard);

export const hasPreparedTemplateFunnel = (
  funnel: Pick<
    FunnelConfig,
    | 'designTemplateType'
    | 'sections'
    | 'universalStyleEngineVersion'
    | 'universalCatalogReleaseId'
    | 'universalPageStyleId'
    | 'universalCreationRequestId'
  >,
) => funnel.universalStyleEngineVersion === 'universal_slots_v1'
  ? Boolean(
      funnel.universalCatalogReleaseId &&
      funnel.universalPageStyleId &&
      funnel.universalCreationRequestId &&
      funnel.sections.some((section) => !section.isStandard && section.enabled)
    )
  : Boolean(funnel.designTemplateType) &&
    funnel.sections.some((section) => !section.isStandard && section.enabled);
